import React, { useEffect, useMemo, useState } from "react";
import {
  Table, Button, Modal, Form, Input, Switch, Select, Tag,
  message, Space, Tooltip, Divider, Radio, Checkbox, Spin,
} from "antd";
import {
  PlusOutlined, EditOutlined, KeyOutlined, SyncOutlined, GlobalOutlined,
} from "@ant-design/icons";
import { userApi, roleApi, tenantApi, adminApi, authApi } from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import { useEnvironment } from "../../context/EnvironmentContext";
import useBreakpoint from "../../hooks/useBreakpoint";

const DOMAIN = "qubertech.com";

const CENTRAL_TABLES = [
  { name: "quber_admin_central_permissions", description: "Permission definitions (config:read, config:edit, etc.)" },
  { name: "quber_admin_central_roles", description: "Role definitions (Admin, Editor, Viewer, custom)" },
  { name: "quber_admin_central_role_permissions", description: "Role-to-permission mappings" },
  { name: "quber_admin_central_users", description: "User accounts" },
  { name: "quber_admin_central_user_access", description: "Per-user env + tenant + role assignments" },
];

const ENV_LABELS = { dev: "Development", stage: "Staging", prod: "Production" };

export default function UserManagementPage() {
  const { isAdmin, user: currentUser, updatePermissions, permissions, globalPermissions } = useAuth();
  const { refreshEnvs } = useEnvironment();

  // Environments the current user can administer (holds "users:manage"), and
  // whether they're a platform admin (holds "roles:edit"). User-access edits
  // are scoped to manageEnvs; other envs are shown read-only and never sent.
  const manageEnvs = useMemo(
    () => new Set(
      (permissions || [])
        .filter((p) => p.role?.permissions?.includes("users:manage"))
        .map((p) => p.environment)
    ),
    [permissions]
  );
  const isPlatformAdmin = useMemo(
    () =>
      (globalPermissions || []).includes("roles:edit") ||
      (permissions || []).some((p) => p.role?.permissions?.includes("roles:edit")),
    [permissions, globalPermissions]
  );
  const { isMobile } = useBreakpoint();
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [globalPermsList, setGlobalPermsList] = useState([]);
  const [loading, setLoading] = useState(false);

  // Create user modal
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm] = Form.useForm();
  const [createLoading, setCreateLoading] = useState(false);

  // Edit user modal
  const [editOpen, setEditOpen] = useState(false);
  const [editForm] = Form.useForm();
  const [editUser, setEditUser] = useState(null);
  const [editLoading, setEditLoading] = useState(false);

  // Access editor modal
  const [accessOpen, setAccessOpen] = useState(false);
  const [accessUser, setAccessUser] = useState(null);
  const [accessEntries, setAccessEntries] = useState([]);
  const [selectedGlobalPermIds, setSelectedGlobalPermIds] = useState([]);
  const [accessLoading, setAccessLoading] = useState(false);
  const [tenantsByEnv, setTenantsByEnv] = useState({});
  const [loadingTenants, setLoadingTenants] = useState({});

  // Migration modal
  const [migrateOpen, setMigrateOpen] = useState(false);
  const [migrateTargets, setMigrateTargets] = useState([]);
  const [migrateLoading, setMigrateLoading] = useState(false);
  const [migrateResults, setMigrateResults] = useState(null);
  const [sourceEnv, setSourceEnv] = useState(null);
  const [migrateInfoLoading, setMigrateInfoLoading] = useState(false);

  // Configured envs from server (only envs with DB config)
  const [configuredEnvs, setConfiguredEnvs] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [usersRes, rolesRes, migrateInfoRes, globalPermsRes] = await Promise.all([
        userApi.getAll(),
        roleApi.getAll(),
        adminApi.getMigrateInfo(),
        userApi.getGlobalPermissions(),
      ]);
      setUsers(usersRes.data);
      setRoles(rolesRes.data);
      setConfiguredEnvs(migrateInfoRes.data.configuredEnvs || []);
      setSourceEnv(migrateInfoRes.data.sourceEnv || null);
      setGlobalPermsList(globalPermsRes.data);
    } catch {
      message.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  // Fetch tenants for a specific env
  const fetchTenantsForEnv = async (env) => {
    if (tenantsByEnv[env]) return;
    setLoadingTenants((prev) => ({ ...prev, [env]: true }));
    try {
      const res = await tenantApi.getAllForEnv(env);
      setTenantsByEnv((prev) => ({ ...prev, [env]: res.data }));
    } catch {
      message.error(`Failed to fetch tenants for ${env}`);
    } finally {
      setLoadingTenants((prev) => ({ ...prev, [env]: false }));
    }
  };

  // ── Create User ────────────────────────────────────────────────────────────
  const handleCreate = async () => {
    try {
      const values = await createForm.validateFields();
      setCreateLoading(true);
      await userApi.create({ email: values.email, name: values.name });
      message.success("User created");
      setCreateOpen(false);
      createForm.resetFields();
      fetchData();
    } catch (err) {
      if (err.response) message.error(err.response.data?.error || "Failed to create user");
    } finally {
      setCreateLoading(false);
    }
  };

  // ── Edit User ──────────────────────────────────────────────────────────────
  const openEditUser = (user) => {
    setEditUser(user);
    editForm.setFieldsValue({ name: user.name, is_active: user.is_active });
    setEditOpen(true);
  };

  const handleEdit = async () => {
    try {
      const values = await editForm.validateFields();
      setEditLoading(true);
      await userApi.update(editUser.id, values);
      message.success("User updated");
      setEditOpen(false);
      fetchData();
    } catch (err) {
      if (err.response) message.error(err.response.data?.error || "Failed to update user");
    } finally {
      setEditLoading(false);
    }
  };

  // ── Access Editor ──────────────────────────────────────────────────────────
  // Per-env entry shape:
  //   {
  //     environment, enabled,
  //     baseEnabled,            // is there a "default role" block at all?
  //     baseRoleId,             // role for the default block
  //     baseScope: "all" | "specific",
  //                             // "all"      -> stored as one wildcard row (tenant_name = NULL).
  //                             //               Future tenants get this role automatically.
  //                             // "specific" -> stored as N rows, one per chosen tenant.
  //                             //               Future tenants do NOT get access.
  //     baseTenants,            // tenant names included in scope=specific
  //     overrides,              // [{tenantName, roleId}] — exception rows with a different role
  //   }
  //
  // DB rule (unique key (user, env, tenant_name)) is preserved by save logic:
  // overrides win over the base block for the same tenant.
  const openAccessEditor = (user) => {
    setAccessUser(user);
    const entries = configuredEnvs.map((env) => {
      const envAccess = user.access.filter((a) => a.environment === env);
      if (envAccess.length === 0) {
        return {
          environment: env, enabled: false,
          baseEnabled: false, baseRoleId: null, baseScope: "all", baseTenants: [],
          overrides: [],
        };
      }
      const wildcardRow = envAccess.find((a) => !a.tenant_name);
      const specificRows = envAccess.filter((a) => a.tenant_name);

      if (wildcardRow) {
        // Wildcard present: base = all + that role. Every specific row is
        // necessarily an override (different role would be the whole point).
        return {
          environment: env, enabled: true,
          baseEnabled: true, baseRoleId: wildcardRow.role_id, baseScope: "all", baseTenants: [],
          overrides: specificRows.map((a) => ({ tenantName: a.tenant_name, roleId: a.role_id })),
        };
      }

      // No wildcard. If every specific row shares a role, treat that role
      // as the "base" with scope=specific. Otherwise no base — everything
      // is an override.
      const roleIds = [...new Set(specificRows.map((a) => a.role_id))];
      if (roleIds.length === 1) {
        return {
          environment: env, enabled: true,
          baseEnabled: true, baseRoleId: roleIds[0], baseScope: "specific",
          baseTenants: specificRows.map((a) => a.tenant_name),
          overrides: [],
        };
      }
      return {
        environment: env, enabled: true,
        baseEnabled: false, baseRoleId: null, baseScope: "all", baseTenants: [],
        overrides: specificRows.map((a) => ({ tenantName: a.tenant_name, roleId: a.role_id })),
      };
    });
    setAccessEntries(entries);
    setSelectedGlobalPermIds(user.global_permission_ids || []);
    setAccessOpen(true);
    // Only manageable envs are editable, so only those need tenant lists
    // (fetching tenants for an env the actor can't access would 403).
    configuredEnvs.filter((env) => manageEnvs.has(env)).forEach(fetchTenantsForEnv);
  };

  const updateAccessEntry = (env, field, value) => {
    setAccessEntries((prev) =>
      prev.map((e) => (e.environment === env ? { ...e, [field]: value } : e))
    );
  };

  const addOverride = (env) => {
    setAccessEntries((prev) =>
      prev.map((e) =>
        e.environment === env
          ? { ...e, overrides: [...(e.overrides || []), { tenantName: null, roleId: null }] }
          : e
      )
    );
  };

  const updateOverride = (env, idx, field, value) => {
    setAccessEntries((prev) =>
      prev.map((e) =>
        e.environment === env
          ? { ...e, overrides: e.overrides.map((o, i) => (i === idx ? { ...o, [field]: value } : o)) }
          : e
      )
    );
  };

  const removeOverride = (env, idx) => {
    setAccessEntries((prev) =>
      prev.map((e) =>
        e.environment === env
          ? { ...e, overrides: e.overrides.filter((_, i) => i !== idx) }
          : e
      )
    );
  };

  const handleSaveAccess = async () => {
    const access = [];
    for (const entry of accessEntries) {
      // Never submit envs the actor doesn't manage — the server preserves
      // those rows, and sending them would be rejected.
      if (!manageEnvs.has(entry.environment)) continue;
      if (!entry.enabled) continue;

      // Tenants this user has an override for — overrides win at the
      // (env, tenant) level, so skip base rows for these.
      const overrideTenants = new Set(
        (entry.overrides || []).filter((o) => o.tenantName && o.roleId).map((o) => o.tenantName)
      );

      // Base block: either a single wildcard row (scope=all) or one row
      // per tenant in baseTenants (scope=specific), minus anything that
      // has an override.
      if (entry.baseEnabled && entry.baseRoleId) {
        if (entry.baseScope === "all") {
          access.push({ environment: entry.environment, tenant_name: null, role_id: entry.baseRoleId });
        } else {
          for (const t of entry.baseTenants || []) {
            if (overrideTenants.has(t)) continue;
            access.push({ environment: entry.environment, tenant_name: t, role_id: entry.baseRoleId });
          }
        }
      }

      // Overrides — already de-duped by tenant.
      const seen = new Set();
      for (const ov of entry.overrides || []) {
        if (!ov.tenantName || !ov.roleId) continue;
        if (seen.has(ov.tenantName)) continue;
        seen.add(ov.tenantName);
        access.push({ environment: entry.environment, tenant_name: ov.tenantName, role_id: ov.roleId });
      }
    }
    setAccessLoading(true);
    try {
      await userApi.updateAccess(accessUser.id, { access });
      // Global permissions are platform-admin only; skip the call otherwise
      // (the server would reject it and the access update already succeeded).
      if (isPlatformAdmin) {
        await userApi.updateGlobalPermissions(accessUser.id, { permission_ids: selectedGlobalPermIds });
      }
      message.success("Access updated");
      setAccessOpen(false);
      fetchData();

      // If editing own access, refresh permissions + env selector immediately
      if (accessUser.id === currentUser?.id) {
        try {
          const res = await authApi.me();
          updatePermissions(res.data.permissions, res.data.globalPermissions);
          refreshEnvs();
        } catch {
          message.info("Permissions updated — you may need to re-login for full effect");
        }
      }
    } catch (err) {
      message.error(err.response?.data?.error || "Failed to update access");
    } finally {
      setAccessLoading(false);
    }
  };

  // ── Migration ──────────────────────────────────────────────────────────────
  const openMigrateModal = async () => {
    setMigrateOpen(true);
    setMigrateResults(null);
    setMigrateTargets([]);
    // Source env may not be loaded yet if fetchData failed partially
    if (!sourceEnv) {
      setMigrateInfoLoading(true);
      try {
        const res = await adminApi.getMigrateInfo();
        setSourceEnv(res.data.sourceEnv);
      } catch {
        message.error("Failed to load migration info");
      } finally {
        setMigrateInfoLoading(false);
      }
    }
  };

  // Target envs = all configured envs minus the source RBAC env
  const targetEnvOptions = configuredEnvs
    .filter((e) => e !== sourceEnv)
    .map((e) => ({ label: ENV_LABELS[e] || e, value: e }));

  const handleMigrate = async () => {
    if (migrateTargets.length === 0) return;
    setMigrateLoading(true);
    setMigrateResults(null);
    try {
      const res = await adminApi.migrateCentral({ targetEnvs: migrateTargets });
      setMigrateResults(res.data.results);
      message.success("Migration completed");
    } catch (err) {
      message.error(err.response?.data?.error || "Migration failed");
    } finally {
      setMigrateLoading(false);
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const ENV_COLORS = { dev: "green", stage: "orange", prod: "red" };

  // Group a user's access rows per env into a shape that handles mixed
  // roles (a wildcard for the env plus per-tenant overrides on different
  // roles). Each env gets:
  //   wildcardRole: string | null      — the role used by the "all tenants" row
  //   byRole: { [roleName]: [tenants] } — per-tenant overrides, grouped by role
  const buildEnvGroups = (access) => {
    const groups = {};
    (access || []).forEach((a) => {
      if (!groups[a.environment]) groups[a.environment] = { wildcardRole: null, byRole: {} };
      const roleName = a.role_name || `Role #${a.role_id}`;
      if (a.tenant_name) {
        if (!groups[a.environment].byRole[roleName]) groups[a.environment].byRole[roleName] = [];
        groups[a.environment].byRole[roleName].push(a.tenant_name);
      } else {
        groups[a.environment].wildcardRole = roleName;
      }
    });
    Object.values(groups).forEach((g) => {
      Object.values(g.byRole).forEach((arr) => arr.sort((a, b) => a.localeCompare(b)));
    });
    return groups;
  };

  // Short summary string for an env's access — e.g. "Viewer", "Editor (3)",
  // "Viewer + 2 overrides". Used in the table tag and mobile card tag.
  const summarizeEnv = (group) => {
    const overrideRoles = Object.keys(group.byRole);
    const overrideTenantTotal = overrideRoles.reduce((sum, r) => sum + group.byRole[r].length, 0);
    if (group.wildcardRole && overrideRoles.length === 0) return group.wildcardRole;
    if (group.wildcardRole) return `${group.wildcardRole} + ${overrideTenantTotal} override${overrideTenantTotal === 1 ? "" : "s"}`;
    if (overrideRoles.length === 1) return `${overrideRoles[0]} (${overrideTenantTotal})`;
    return `${overrideRoles.length} roles (${overrideTenantTotal})`;
  };

  // ── Table columns ──────────────────────────────────────────────────────────
  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      render: (text, record) => (
        <div>
          <div className="font-medium">{text}</div>
          <div className="text-slate-500 text-[12px]">{record.email}</div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "is_active",
      width: 80,
      render: (val) => (
        <Tag color={val ? "green" : "default"}>{val ? "Active" : "Inactive"}</Tag>
      ),
    },
    {
      title: "Environments",
      dataIndex: "access",
      render: (access, record) => {
        const envGroups = buildEnvGroups(access);
        const hasGlobal = record.global_permission_ids?.length > 0;
        return (
          <div className="flex flex-wrap gap-1">
            {!access?.length && !hasGlobal && <span className="text-slate-400">No access</span>}
            {Object.entries(envGroups).map(([env, group]) => (
              <Tag key={env} color={ENV_COLORS[env] || "default"} className="!m-0">
                {env.toUpperCase()}: {summarizeEnv(group)}
              </Tag>
            ))}
            {hasGlobal && (
              <Tag icon={<GlobalOutlined />} color="purple" className="!m-0 !text-[11px]">
                Global ({record.global_permission_ids.length})
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: "Actions",
      width: 120,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Edit user">
            <Button type="text" icon={<EditOutlined />} onClick={() => openEditUser(record)} />
          </Tooltip>
          <Tooltip title="Manage access">
            <Button type="text" icon={<KeyOutlined />} onClick={() => openAccessEditor(record)} />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // ── Expandable row: per-env access detail ───────────────────────────────────
  // Each env block shows the wildcard role (if any) plus each override role
  // with its tenants. Lets mixed-role envs be inspected at a glance.
  const expandedRowRender = (record) => {
    const envGroups = buildEnvGroups(record.access);
    const envEntries = Object.entries(envGroups);
    const globalLabels = (record.global_permission_ids || [])
      .map((id) => globalPermsList.find((p) => p.id === id)?.label)
      .filter(Boolean);

    if (envEntries.length === 0 && globalLabels.length === 0) {
      return <span className="text-slate-400">No access configured</span>;
    }
    return (
      <div className="flex gap-6 py-1 flex-wrap">
        {envEntries.map(([env, group]) => (
          <div key={env} className="min-w-[200px]">
            <div className="font-medium text-[12px] text-slate-700 mb-1.5">
              {ENV_LABELS[env] || env}
            </div>
            {group.wildcardRole && (
              <div className="mb-1.5">
                <Tag color="blue" className="!text-[11px] !m-0">
                  All tenants — {group.wildcardRole}
                </Tag>
              </div>
            )}
            {Object.entries(group.byRole).map(([role, tenants]) => (
              <div key={role} className="mb-1.5">
                <div className="text-[11px] text-slate-500 mb-0.5">
                  {role} — {tenants.length} tenant{tenants.length === 1 ? "" : "s"}
                </div>
                <div className="flex flex-wrap gap-[3px]">
                  {tenants.map((t) => (
                    <Tag key={t} className="!text-[11px] !m-0">{t}</Tag>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
        {globalLabels.length > 0 && (
          <div className="min-w-[180px]">
            <div className="font-medium text-[12px] text-slate-700 mb-1">
              Global Permissions
            </div>
            <div className="flex flex-wrap gap-[3px]">
              {globalLabels.map((label) => (
                <Tag key={label} color="purple" className="!text-[11px] !m-0">{label}</Tag>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">User Management</h1>
        <p className="page-subtitle">Manage users, their roles, and environment access</p>
      </div>

      <div className="flex gap-2 mb-4">
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
          Add User
        </Button>
        {isAdmin && (
          <Button icon={<SyncOutlined />} onClick={openMigrateModal}>
            Migrate to Other Envs
          </Button>
        )}
      </div>

      {!isMobile ? (
        <div className="sq-table">
          <Table
            rowKey="id"
            dataSource={users}
            columns={columns}
            loading={loading}
            size="middle"
            pagination={{ defaultPageSize: 15, showSizeChanger: true }}
            expandable={{
              expandedRowRender,
              rowExpandable: (record) => record.access?.length > 0 || record.global_permission_ids?.length > 0,
            }}
          />
        </div>
      ) : (
        <div className="card-list">
          {loading && (
            <div className="empty-state py-6 px-3">
              <div className="empty-state-text">Loading...</div>
            </div>
          )}
          {!loading && users.length === 0 && (
            <div className="empty-state py-6 px-3">
              <div className="empty-state-text">No users</div>
            </div>
          )}
          {!loading && users.map((record) => {
            const envGroups = buildEnvGroups(record.access);
            const hasGlobal = record.global_permission_ids?.length > 0;
            return (
              <div key={record.id} className="card-list-item">
                <div className="flex justify-between items-start gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="card-list-item-title truncate">{record.name}</div>
                    <div className="card-list-item-sub truncate">{record.email}</div>
                  </div>
                  <Tag color={record.is_active ? "green" : "default"} className="!m-0 shrink-0">
                    {record.is_active ? "Active" : "Inactive"}
                  </Tag>
                </div>
                {(Object.keys(envGroups).length > 0 || hasGlobal) ? (
                  <div className="card-list-item-meta">
                    {Object.entries(envGroups).map(([env, group]) => (
                      <Tag key={env} color={ENV_COLORS[env] || "default"} className="!m-0">
                        {env.toUpperCase()}: {summarizeEnv(group)}
                      </Tag>
                    ))}
                    {hasGlobal && (
                      <Tag icon={<GlobalOutlined />} color="purple" className="!m-0 !text-[11px]">
                        Global ({record.global_permission_ids.length})
                      </Tag>
                    )}
                  </div>
                ) : (
                  <span className="text-[12px] text-slate-400">No access</span>
                )}
                <div className="card-list-item-actions">
                  <Button size="small" icon={<EditOutlined />} onClick={() => openEditUser(record)}>Edit</Button>
                  <Button size="small" type="primary" icon={<KeyOutlined />} onClick={() => openAccessEditor(record)}>
                    Manage Access
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create User Modal */}
      <Modal
        title="Add User"
        open={createOpen}
        onOk={handleCreate}
        onCancel={() => { setCreateOpen(false); createForm.resetFields(); }}
        confirmLoading={createLoading}
      >
        <Form form={createForm} layout="vertical">
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: "Email is required" },
              { pattern: new RegExp(`@${DOMAIN}$`), message: `Must be @${DOMAIN}` },
            ]}
          >
            <Input placeholder={`user@${DOMAIN}`} />
          </Form.Item>
          <Form.Item name="name" label="Name">
            <Input placeholder="Full name" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit User Modal */}
      <Modal
        title="Edit User"
        open={editOpen}
        onOk={handleEdit}
        onCancel={() => setEditOpen(false)}
        confirmLoading={editLoading}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      {/* Access Editor Modal */}
      <Modal
        title={`Manage Access — ${accessUser?.name || ""}`}
        open={accessOpen}
        onOk={handleSaveAccess}
        onCancel={() => setAccessOpen(false)}
        confirmLoading={accessLoading}
        width={640}
      >
        {accessEntries.map((entry) => {
          const manageable = manageEnvs.has(entry.environment);
          if (!manageable) {
            const group = buildEnvGroups(accessUser?.access || [])[entry.environment];
            return (
              <div key={entry.environment} className="mb-4">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <strong className="text-slate-400">{ENV_LABELS[entry.environment] || entry.environment}</strong>
                  <Tag className="!m-0">Read only</Tag>
                  <span className="text-[11px] text-slate-400">— you don't manage this environment</span>
                </div>
                <div className="pl-6 text-[12px] text-slate-500">
                  {group ? summarizeEnv(group) : "No access"}
                </div>
                <Divider className="!my-3" />
              </div>
            );
          }
          return (
          <div key={entry.environment} className="mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Checkbox
                checked={entry.enabled}
                onChange={(e) => updateAccessEntry(entry.environment, "enabled", e.target.checked)}
              >
                <strong>{ENV_LABELS[entry.environment] || entry.environment}</strong>
              </Checkbox>
            </div>

            {entry.enabled && (
              <div className="pl-6">
                {/* Base role block: a role applied to either all tenants in
                    the env (incl. future) or a specific list. */}
                <div className="mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Checkbox
                      checked={entry.baseEnabled}
                      onChange={(e) => updateAccessEntry(entry.environment, "baseEnabled", e.target.checked)}
                    >
                      Base role
                    </Checkbox>
                    {entry.baseEnabled && (
                      <Select
                        value={entry.baseRoleId}
                        onChange={(val) => updateAccessEntry(entry.environment, "baseRoleId", val)}
                        className="w-[180px]"
                        placeholder="Select role"
                        options={roles.map((r) => ({ label: r.name, value: r.id }))}
                        size="small"
                      />
                    )}
                  </div>
                  {entry.baseEnabled && (
                    <div className="mt-2 pl-6">
                      <Radio.Group
                        value={entry.baseScope}
                        onChange={(e) => updateAccessEntry(entry.environment, "baseScope", e.target.value)}
                        size="small"
                      >
                        <Radio value="all">
                          All tenants
                          <span className="text-[11px] text-slate-400 ml-1">
                            — incl. future
                          </span>
                        </Radio>
                        <Radio value="specific">
                          Specific tenants
                        </Radio>
                      </Radio.Group>
                      {entry.baseScope === "specific" && (
                        <div className="mt-1.5">
                          <Select
                            mode="multiple"
                            value={entry.baseTenants}
                            onChange={(val) => updateAccessEntry(entry.environment, "baseTenants", val)}
                            className="w-full"
                            placeholder="Select tenants for the base role..."
                            loading={loadingTenants[entry.environment]}
                            showSearch
                            filterOption={(input, option) => (option.label || "").toLowerCase().includes(input.toLowerCase())}
                            options={(tenantsByEnv[entry.environment] || []).map((t) => ({
                              label: t.subdomain || t.name,
                              value: t.name,
                            }))}
                            size="small"
                          />
                          {entry.baseTenants.length === 0 && (
                            <div className="text-[11px] text-amber-600 mt-1">
                              Pick at least one tenant, or switch back to "All tenants".
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Per-tenant overrides — different role on a specific tenant.
                    Wins over the base block for that tenant. */}
                <div className="mb-2">
                  <div className="text-[12px] font-medium text-slate-600 mb-1.5">
                    Per-tenant overrides
                    {entry.overrides.length > 0 && (
                      <span className="text-slate-400 font-normal ml-1.5">
                        ({entry.overrides.length})
                      </span>
                    )}
                  </div>
                  {entry.overrides.length === 0 && !entry.baseEnabled && (
                    <div className="text-[12px] text-amber-600 mb-1.5">
                      Add a base role or at least one tenant override to grant access in this environment.
                    </div>
                  )}
                  {entry.overrides.map((ov, idx) => {
                    // Exclude tenants already used by another override OR
                    // by the base block in scope=specific mode (avoids
                    // (env, tenant) collision at save time).
                    const reservedByBase = entry.baseEnabled && entry.baseScope === "specific"
                      ? new Set(entry.baseTenants)
                      : new Set();
                    const reservedByOtherOverride = new Set(
                      entry.overrides.filter((o, i) => i !== idx && o.tenantName).map((o) => o.tenantName)
                    );
                    return (
                      <div key={idx} className="flex gap-1.5 mb-1.5 items-center flex-wrap">
                        <Select
                          value={ov.tenantName || undefined}
                          onChange={(val) => updateOverride(entry.environment, idx, "tenantName", val)}
                          className="flex-[1_1_200px] min-w-[160px]"
                          placeholder="Tenant"
                          loading={loadingTenants[entry.environment]}
                          showSearch
                          filterOption={(input, option) => (option.label || "").toLowerCase().includes(input.toLowerCase())}
                          options={(tenantsByEnv[entry.environment] || [])
                            .filter((t) => t.name === ov.tenantName || (!reservedByOtherOverride.has(t.name) && !reservedByBase.has(t.name)))
                            .map((t) => ({ label: t.subdomain || t.name, value: t.name }))}
                          size="small"
                        />
                        <Select
                          value={ov.roleId || undefined}
                          onChange={(val) => updateOverride(entry.environment, idx, "roleId", val)}
                          className="flex-[1_1_140px] min-w-[120px]"
                          placeholder="Role"
                          options={roles.map((r) => ({ label: r.name, value: r.id }))}
                          size="small"
                        />
                        <Button
                          type="text"
                          danger
                          size="small"
                          onClick={() => removeOverride(entry.environment, idx)}
                          aria-label="Remove override"
                        >
                          ×
                        </Button>
                      </div>
                    );
                  })}
                  <Button
                    type="dashed"
                    size="small"
                    onClick={() => addOverride(entry.environment)}
                    disabled={loadingTenants[entry.environment] || (tenantsByEnv[entry.environment] || []).length === 0}
                  >
                    + Add tenant override
                  </Button>
                </div>
              </div>
            )}
            <Divider className="!my-3" />
          </div>
          );
        })}

        {isPlatformAdmin && globalPermsList.length > 0 && (
          <div>
            <div className="font-medium mb-2 flex items-center gap-1.5">
              <GlobalOutlined className="text-violet-600" />
              Global Permissions
            </div>
            <div className="pl-1 mb-2">
              {globalPermsList.map((p) => (
                <Checkbox
                  key={p.id}
                  checked={selectedGlobalPermIds.includes(p.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedGlobalPermIds((prev) => [...prev, p.id]);
                    } else {
                      setSelectedGlobalPermIds((prev) => prev.filter((id) => id !== p.id));
                    }
                  }}
                  className="!block !ml-0 mb-1"
                >
                  {p.label} <span className="text-slate-400 text-[11px]">({p.key})</span>
                </Checkbox>
              ))}
            </div>
            <Divider className="!my-3" />
          </div>
        )}
      </Modal>

      {/* Migration Modal */}
      <Modal
        title="Migrate Central Tables"
        open={migrateOpen}
        onCancel={() => { setMigrateOpen(false); setMigrateResults(null); }}
        width={600}
        footer={[
          <Button key="close" onClick={() => { setMigrateOpen(false); setMigrateResults(null); }}>
            Close
          </Button>,
          <Button key="migrate" type="primary" onClick={handleMigrate} loading={migrateLoading}
            disabled={migrateTargets.length === 0}>
            Migrate
          </Button>,
        ]}
      >
        {migrateInfoLoading ? (
          <div className="text-center py-6"><Spin /></div>
        ) : (
          <>
            <p className="mb-1">
              Copy all central RBAC tables from{" "}
              <Tag color="blue">{ENV_LABELS[sourceEnv] || sourceEnv}</Tag>{" "}
              to target environments.
            </p>

            {/* Tables that will be migrated */}
            <div className="mb-4">
              <div className="font-medium mb-1.5 text-[13px] text-slate-700">
                Tables to be migrated:
              </div>
              <div className="bg-slate-50 border border-solid border-slate-200 rounded-md py-2 px-3 text-[12px] text-slate-600">
                {CENTRAL_TABLES.map((t) => (
                  <div key={t.name} className="flex justify-between py-[3px] gap-4">
                    <code className="text-indigo-500 text-[11px] whitespace-nowrap">{t.name}</code>
                    <span className="text-slate-400 text-right">{t.description}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Target env selection */}
            <div className="mb-3">
              <div className="font-medium mb-1.5 text-[13px] text-slate-700">
                Select target environments:
              </div>
              {targetEnvOptions.length > 0 ? (
                <Checkbox.Group
                  options={targetEnvOptions}
                  value={migrateTargets}
                  onChange={setMigrateTargets}
                />
              ) : (
                <span className="text-slate-400 text-[13px]">
                  No other environments configured to migrate to.
                </span>
              )}
            </div>
          </>
        )}

        {migrateResults && (
          <div className="mt-4">
            <Divider />
            <div className="font-medium mb-2 text-[13px] text-slate-700">Results:</div>
            {migrateResults.map((r, i) => (
              <div key={i} className="mb-2.5 py-2 px-3 bg-slate-50 rounded-md border border-solid border-slate-200">
                <Tag color={r.status === "completed" ? "green" : r.status === "skipped" ? "blue" : "red"} className="mb-1.5">
                  {ENV_LABELS[r.env] || r.env}: {r.status}
                </Tag>
                {r.tables && (
                  <div className="flex flex-wrap gap-1">
                    {r.tables.map((t, j) => (
                      <Tag key={j} color={t.status === "synced" ? "green" : "red"} className="!text-[11px]">
                        {t.table}: {t.status} {t.rowCount != null ? `(${t.rowCount} rows)` : ""}
                      </Tag>
                    ))}
                  </div>
                )}
                {r.message && <div className="text-slate-500 text-[12px] mt-1">{r.message}</div>}
                {r.error && <div className="text-red-500 text-[12px] mt-1">{r.error}</div>}
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}
